import { Col, Row } from "react-grid-system";

import { ColumnSearch } from "components/ReadLayer/ColumnSearch";
import Page from "components/Page";
import PageHeader from "components/PageHeader";
import { PropertyListings } from "./PropertyListings";
import { ReadLayerWrapper } from "components/ReadLayer/ReadLayerWrapper";
import { SearchField } from "components/ReadLayer/SearchField";
import { Stack } from "@adfenix/alexandria";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import useUserInfo from "hooks/useUserInfo";
import { userSelectedHq } from "atoms/auth";
import { ColumnDropdown } from "components/ReadLayer/ColumnDropdown";
import { subDays, subMonths, subYears } from "date-fns";
import { ReadLayerQueryFilter } from "api/services";
import { ResetButton } from "components/ReadLayer/ResetButton";
import { LoginCard } from "./LoginCard";

export const PropertyList = () => {
  const { t } = useTranslation();
  const selectedHq = useRecoilValue(userSelectedHq);

  const { offices, accessLevel } = useUserInfo();
  const timePeriodOptions = [
    {
      label: t("listings.filters.timeperiod.last24hours"),
      value: "last24hours"
    },
    {
      label: t("listings.filters.timeperiod.last7days"),
      value: "last7days"
    },
    {
      label: t("listings.filters.timeperiod.last30days"),
      value: "last30days"
    },
    {
      label: t("listings.filters.timeperiod.last90days"),
      value: "last90days"
    },
    {
      label: t("listings.filters.timeperiod.last12months"),
      value: "last12months"
    }
  ];

  const filterRewrite = (filter: ReadLayerQueryFilter) => {
    if (filter.column !== "created") return filter;

    let fromDateTimeUtc = "";

    if (filter.displayValue === "last24hours")
      fromDateTimeUtc = subDays(new Date(), 1).toISOString();
    else if (filter.displayValue === "last7days")
      fromDateTimeUtc = subDays(new Date(), 7).toISOString();
    else if (filter.displayValue === "last30days")
      fromDateTimeUtc = subMonths(new Date(), 1).toISOString();
    else if (filter.displayValue === "last90days")
      fromDateTimeUtc = subMonths(new Date(), 3).toISOString();
    else if (filter.displayValue === "last12months")
      fromDateTimeUtc = subYears(new Date(), 1).toISOString();

    const now = new Date();

    return {
      column: filter.column,
      displayValue: filter.displayValue,
      fromDateTimeUtc,
      toDateTimeUtc: now.toISOString()
    };
  };

  return (
    <Page
      header={<PageHeader title={t("listings.heading.title")} />}
      style={{ maxWidth: 1280, minHeight: 600 }}
      fullWidth
    >
      <br />
      <ReadLayerWrapper
        serviceName="property"
        indexName="property"
        sortOnColumn="created"
        sortAscending={false}
        filters={[{ column: "hq.id", value: selectedHq?.id }]}
        filterRewrite={filterRewrite}
      >
        <Stack spacing="m">
          <SearchField
            minWidth={690}
            placeholder={t("listings.page.search.input.title")}
            columns={[
              "location.address",
              "id",
              "agent.email",
              "url",
              "nonCanonicalUrls"
            ]}
          />

          <Row>
            <Col>
              <ColumnDropdown
                column="created"
                placeholder={t("listings.filters.timeperiod.title")}
                options={timePeriodOptions}
              />
            </Col>

            {(accessLevel === "HQ" ||
              accessLevel === "Multi-Brand" ||
              (accessLevel === "Office" && (offices?.length ?? 0) > 1)) && (
              <>
                <Col>
                  <ColumnSearch
                    column="office.active"
                    placeholder={t(
                      "listings.filters.office.status.placeholder"
                    )}
                  />
                </Col>

                <Col>
                  <ColumnSearch
                    column="office.name"
                    placeholder={t("listings.filters.office.title")}
                    showDefaultOptions={false}
                    filterColumn={"hq.id"}
                  />
                </Col>
              </>
            )}

            {(accessLevel === "HQ" ||
              accessLevel === "Multi-Brand" ||
              accessLevel === "Office") && (
              <Col>
                <ColumnSearch
                  column="agent.name"
                  placeholder={t("listings.filters.agent.title")}
                  showDefaultOptions={false}
                  filterColumn={"hq.id"}
                />
              </Col>
            )}

            <Col>
              <ColumnSearch
                column="propertyStatusText"
                placeholder={t("listings.filters.property.status.title")}
                filterColumn={"hq.id"}
              />
            </Col>
            <Col>
              <ColumnSearch
                column="transactionType"
                placeholder={"Transaction type"}
                filterColumn={"hq.id"}
              />
            </Col>
            <Col>
              <ResetButton />
            </Col>
          </Row>

          <PropertyListings />

          <LoginCard />
        </Stack>
      </ReadLayerWrapper>
    </Page>
  );
};
